/**
 * Un parámetro de la URL de esta página.
 *
 * `public/auth-scrub.js` retira de la barra de direcciones, ANTES de que cargue la
 * analítica, los parámetros que no deben llegar a un tercero —el token de una
 * invitación y el correo prerrellenado— y los deja en `window.__okeyAuthParams`.
 * Aquí se leen de ahí primero y de la URL después: el segundo camino mantiene el
 * servidor de desarrollo y un index.html cacheado sin el scrubber.
 */
const STASH_KEY = '__okeyAuthParams';

export function paramDeLaUrl(key: string): string | null {
  try {
    const stash = (window as unknown as Record<string, Record<string, string> | undefined>)[STASH_KEY];
    const guardado = stash ? stash[key] : undefined;
    if (typeof guardado === 'string' && guardado.trim()) return guardado;
    const enLaUrl = new URL(window.location.href).searchParams.get(key);
    return enLaUrl && enLaUrl.trim() ? enLaUrl : null;
  } catch {
    return null;
  }
}

/**
 * Una ruta de ESTA app con unos parámetros que no deben ir en la URL.
 *
 * Los enlaces alta ↔ acceso se llevaban la invitación y el correo en la query
 * de un `<router-link>`: una navegación SPA que el scrubber nunca ve, y GA4 y
 * PostHog informaban de `page_path` y `$current_url` con el token y el correo
 * (R2_INVITE-1, 2026-09-19). Los parámetros se retienen en el mismo depósito
 * que llena el scrubber —de donde ya los lee `paramDeLaUrl`— y la ruta viaja
 * limpia. Para una navegación con recarga (`window.location`) NO vale: el
 * depósito muere con el documento y ahí el parámetro va en la URL, que el
 * scrubber retira al cargar.
 */
export function rutaConParametrosRetenidos(
  path: string,
  params: Record<string, string | null | undefined>,
): string {
  const retenidos = Object.fromEntries(
    Object.entries(params).filter((par): par is [string, string] => typeof par[1] === 'string' && par[1].trim() !== ''),
  );
  if (Object.keys(retenidos).length === 0) return path;
  try {
    const ventana = window as unknown as Record<string, Record<string, string> | undefined>;
    ventana[STASH_KEY] = { ...(ventana[STASH_KEY] ?? {}), ...retenidos };
  } catch {
    /* sin ventana: la ruta sigue siendo válida */
  }
  return path;
}
